import { Component, type ReactNode } from "react";

/**
 * Keeps one broken page from taking the console with it.
 *
 * React unmounts the whole tree when a render throws, which shows as a blank
 * black screen with nothing to click and nothing said - the worst possible
 * way to learn that one section has a bug, because it looks like the server
 * is down. This catches the throw, keeps the rail and the rest of the console
 * alive, and says what happened.
 *
 * `resetKey` changes when the route does, so navigating away from the broken
 * page clears the error rather than leaving it stuck.
 */
interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class Boundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error): void {
    // Kept in the console log as well: the message on screen is for the
    // person, the stack is for whoever they send it to.
    console.error("page failed to render:", error);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="content">
        <div className="empty">
          <h3>This page could not be shown</h3>
          <p style={{ margin: "0 auto 1rem", maxWidth: "48ch" }}>
            Something in it went wrong. The rest of the console still works, so
            you can carry on elsewhere &mdash; but this is a bug and worth reporting.
          </p>
          <pre className="codeblock" style={{ textAlign: "left", margin: "0 auto 1rem" }}>
            {error.message || String(error)}
          </pre>
          <button className="btn btn--primary" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
        </div>
      </div>
    );
  }
}
